import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import { toast } from "react-toastify";
import { Constants } from "../../../constants/Constants";
import { PathName } from "../../../constants/PathName";
import {
	useCreateFormBuilderSelectMutation,
	useGetFormBuilderSelectQuery,
	useUpdateFormBuilderSelectMutation,
} from "../../../generated/nuggetKmsApi/graphql";

interface SelectOptionState {
	name: string;
	description: string;
	sortOrder: number;
}

interface SelectState {
	name: string;
	options: SelectOptionState[];
}

const createEmptyOption = (sortOrder: number): SelectOptionState => ({
	name: "",
	description: "",
	sortOrder,
});

const createEmptySelect = (): SelectState => ({
	name: "",
	options: [createEmptyOption(1)],
});

const toNumber = (value: string) => {
	const parsed = Number(value);
	return Number.isNaN(parsed) ? 0 : parsed;
};

export const FormBuilderSelectEdit = () => {
	const { selectId } = useParams<{ selectId?: string }>();
	const history = useHistory();
	const [selectState, setSelectState] = useState<SelectState>(createEmptySelect());

	const isEdit = Boolean(selectId);

	const { data, loading } = useGetFormBuilderSelectQuery({
		skip: !isEdit,
		variables: { id: Number(selectId) },
		context: { clientName: Constants.APOLLO_CLIENT_NUGGET_KMS_API_NAME },
	});

	const [createSelect, { loading: creating }] = useCreateFormBuilderSelectMutation({
		context: { clientName: Constants.APOLLO_CLIENT_NUGGET_KMS_API_NAME },
	});

	const [updateSelect, { loading: updating }] = useUpdateFormBuilderSelectMutation({
		context: { clientName: Constants.APOLLO_CLIENT_NUGGET_KMS_API_NAME },
	});

	useEffect(() => {
		if (!data?.getFormBuilderSelect) {
			return;
		}
		const existing = data.getFormBuilderSelect;
		setSelectState({
			name: existing.name,
			options: existing.options.map((option) => ({
				name: option.name,
				description: option.description ?? "",
				sortOrder: option.sortOrder,
			})),
		});
	}, [data]);

	const updateOption = (index: number, patch: Partial<SelectOptionState>) => {
		setSelectState((prev) => {
			const next = [...prev.options];
			next[index] = { ...next[index], ...patch };
			return { ...prev, options: next };
		});
	};

	const addOption = () => {
		setSelectState((prev) => ({
			...prev,
			options: [...prev.options, createEmptyOption(prev.options.length + 1)],
		}));
	};

	const removeOption = (index: number) => {
		setSelectState((prev) => ({
			...prev,
			options: prev.options.filter((_, optionIndex) => optionIndex !== index),
		}));
	};

	const validate = () => {
		if (!selectState.name.trim()) {
			toast.error("Select name is required.");
			return false;
		}
		const optionNames = new Set<string>();
		for (const option of selectState.options) {
			if (!option.name.trim() || !option.sortOrder) {
				toast.error("Option name and sort order are required.");
				return false;
			}
			const key = option.name.trim().toLowerCase();
			if (optionNames.has(key)) {
				toast.error("Option names must be unique within a select.");
				return false;
			}
			optionNames.add(key);
		}
		return true;
	};

	const handleSave = async () => {
		if (!validate()) {
			return;
		}
		const input = {
			name: selectState.name.trim(),
			options: selectState.options.map((option) => ({
				name: option.name.trim(),
				description: option.description.trim() || null,
				sortOrder: option.sortOrder,
			})),
		};

		try {
			if (isEdit) {
				await updateSelect({ variables: { id: Number(selectId), input } });
				toast.success("Select updated.");
			} else {
				const res = await createSelect({ variables: { input } });
				const createdId = res.data?.createFormBuilderSelect.id;
				toast.success("Select created.");
				if (createdId) {
					history.replace(PathName.FORM_BUILDER_SELECTS_EDIT(String(createdId)));
				}
			}
		} catch (error: any) {
			toast.error(error?.message || "Failed to save select.");
		}
	};

	if (loading && isEdit) {
		return <div className="formBuilder-card">Loading select...</div>;
	}

	return (
		<div className="formBuilder-edit">
			<div className="formBuilder-editHeader">
				<button type="button" onClick={() => history.push(PathName.FORM_BUILDER_SELECTS)}>
					Back to selects
				</button>
				<div>
					<h2>{isEdit ? "Edit Select" : "Add Select"}</h2>
					<p>Manage select options for dropdown fields.</p>
				</div>
				<button
					type="button"
					className="formBuilder-button formBuilder-button--primary"
					onClick={handleSave}
					disabled={creating || updating}
				>
					Save
				</button>
			</div>

			<div className="formBuilder-card">
				<label>
					Name
					<input
						type="text"
						value={selectState.name}
						onChange={(event) => setSelectState((prev) => ({ ...prev, name: event.target.value }))}
					/>
				</label>
			</div>

			<div className="formBuilder-card">
				<div className="formBuilder-cardHeader">
					<h3>Options</h3>
				</div>
				{selectState.options.map((option, index) => (
					<div className="formBuilder-subCard" key={`option-${index}`}>
						<div className="formBuilder-cardHeader">
							<h4>Option {index + 1}</h4>
							<button type="button" onClick={() => removeOption(index)}>
								Remove Option
							</button>
						</div>
						<div className="formBuilder-grid">
							<label>
								Name
								<input
									type="text"
									value={option.name}
									onChange={(event) => updateOption(index, { name: event.target.value })}
								/>
							</label>
							<label>
								Description
								<input
									type="text"
									value={option.description}
									onChange={(event) => updateOption(index, { description: event.target.value })}
								/>
							</label>
							<label>
								Sort Order
								<input
									type="number"
									value={option.sortOrder}
									onChange={(event) =>
										updateOption(index, { sortOrder: toNumber(event.target.value) })
									}
								/>
							</label>
						</div>
					</div>
				))}
				<button
					type="button"
					className="formBuilder-button formBuilder-button--secondary"
					onClick={addOption}
				>
					Add Option
				</button>
			</div>
		</div>
	);
};
